'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ResourceDownloadModal from '@/components/ResourceDownloadModal';

type ResourceType = 'Industry Report' | 'Case Study' | 'Whitepaper' | 'Security Brief' | 'Solution Brief';

interface Resource {
  id: string;
  type: ResourceType;
  title: string;
  description: string;
  format: string;
  pages?: string;
  language: string;
  icon: 'chart' | 'book' | 'file-text' | 'shield' | 'lightbulb';
  pdfFile?: string;
  caseStudyUrl?: string;
}

const resources: Resource[] = [
  {
    id: 'apac-state-2026',
    type: 'Industry Report',
    title: 'The State of AI in APAC Language Operations 2026',
    description: 'A market-level view of how enterprise buyers across Asia-Pacific are adopting AI, reshaping language operations, and reassessing provider expectations. Includes findings from 200+ enterprise buyers across 12 APAC markets.',
    format: 'PDF',
    pages: '35 pages',
    language: 'English',
    icon: 'chart',
    pdfFile: '/whitepapers/The_State_of_AI_in_APAC_Language_Operations_2026_Revised.pdf',
  },
  {
    id: 'cs-consumer-electronics',
    type: 'Case Study',
    title: 'Case Study: Consumer Electronics Global Localization',
    description: 'Millions of words delivered annually, 30+ languages, and on-time launch support for leading consumer electronics brands across Asian and global markets.',
    format: 'Web',
    pages: '5 pages',
    language: 'English',
    icon: 'book',
    caseStudyUrl: '/case-studies/consumer-electronics',
  },
  {
    id: 'cs-life-sciences',
    type: 'Case Study',
    title: 'Case Study: Life Sciences eLearning Localization',
    description: 'AI-led production with native-speaker review for regulated eLearning content in 12 languages, with faster delivery and lower cost.',
    format: 'Web',
    pages: '5 pages',
    language: 'English',
    icon: 'book',
    caseStudyUrl: '/case-studies/life-sciences-elearning',
  },
  {
    id: 'cs-legal-translation',
    type: 'Case Study',
    title: 'Case Study: Legal Translation & Document Engineering',
    description: '1 million+ words of Chinese-to-English legal content delivered in 7 days with preserved formatting, numbering, and human-reviewed quality assurance.',
    format: 'Web',
    pages: '5 pages',
    language: 'English',
    icon: 'book',
    caseStudyUrl: '/case-studies/legal-translation',
  },
  {
    id: 'cs-ecommerce',
    type: 'Case Study',
    title: 'Case Study: Multilingual eCommerce Localization',
    description: '20+ language eCommerce localization with 99% first-delivery acceptance, 99% on-time delivery, and custom workflow tooling from Into23.',
    format: 'Web',
    pages: '4 pages',
    language: 'English',
    icon: 'book',
    caseStudyUrl: '/case-studies/ecommerce-localization',
  },
  {
    id: 'cs-dataforce',
    type: 'Case Study',
    title: 'Case Study: DataForce Multilingual Transcription',
    description: '25-language transcription support for AI data operations with trained reviewers, client-tool integration, and Bangalore-led delivery management.',
    format: 'Web',
    pages: '3 pages',
    language: 'English',
    icon: 'book',
    caseStudyUrl: '/case-studies/dataforce-transcription',
  },
  {
    id: 'cs-sify-voiceover',
    type: 'Case Study',
    title: 'Case Study: Sify Digital Multilingual Voiceover',
    description: '18-language eLearning voiceover delivery with 97% first-delivery acceptance, 50% faster timelines, and 99% on-time delivery.',
    format: 'Web',
    pages: '4 pages',
    language: 'English',
    icon: 'book',
    caseStudyUrl: '/case-studies/sify-voiceover',
  },
  {
    id: 'cs-voice-data-ai',
    type: 'Case Study',
    title: 'Case Study: Voice Data for AI Training',
    description: '20-language voice dataset delivery for AI training with auditable consent, standardized recording protocols, and training-ready quality control.',
    format: 'Web',
    pages: '8 pages',
    language: 'English',
    icon: 'book',
    caseStudyUrl: '/case-studies/voice-data-ai',
  },
  {
    id: 'wp-transcription-audio',
    type: 'Whitepaper',
    title: 'Whitepaper: Transcription & Audio Annotation',
    description: 'A practical whitepaper covering multilingual transcription, speech labeling, diarization, emotion annotation, and audio-data QA for enterprise AI programs.',
    format: 'PDF',
    pages: '16 pages',
    language: 'English',
    icon: 'file-text',
    pdfFile: '/whitepapers/Transcription_Audio_Annotation_Into23.pdf',
  },
  {
    id: 'wp-image-recognition',
    type: 'Whitepaper',
    title: 'Whitepaper: Image Recognition Annotation',
    description: 'A practical whitepaper on visual QA, OCR validation, damage detection labeling, and annotation quality control for multimodal AI pipelines.',
    format: 'PDF',
    pages: '17 pages',
    language: 'English',
    icon: 'file-text',
    pdfFile: '/whitepapers/Image_Recognition_Annotation_Into23.pdf',
  },
  {
    id: 'wp-rlhf',
    type: 'Whitepaper',
    title: 'Whitepaper: RLHF Human Feedback for AI Training',
    description: 'A practical whitepaper explaining how structured human feedback supports RLHF programs, safer model alignment, and higher-quality multilingual AI training workflows.',
    format: 'PDF',
    pages: '21 pages',
    language: 'English',
    icon: 'file-text',
    pdfFile: '/whitepapers/RLHF_Human_Feedback_Into23.pdf',
  },
  {
    id: 'wp-llm-evaluation',
    type: 'Whitepaper',
    title: 'Whitepaper: LLM Evaluation & Response Rating',
    description: 'A practical whitepaper on production LLM evaluation, response rating, benchmark limits, and quality-assurance design for enterprise AI teams.',
    format: 'PDF',
    pages: '17 pages',
    language: 'English',
    icon: 'file-text',
    pdfFile: '/whitepapers/LLM_Evaluation_Response_Rating_Into23.pdf',
  },
  {
    id: 'wp-multilingual-ai-eval',
    type: 'Whitepaper',
    title: 'Whitepaper: Multilingual AI Evaluation Programs',
    description: 'A practical whitepaper on designing multilingual evaluation programs with native-speaker assessment, culturally grounded benchmarks, and ongoing QA governance.',
    format: 'PDF',
    pages: '18 pages',
    language: 'English',
    icon: 'file-text',
    pdfFile: '/whitepapers/Multilingual_AI_Evaluation_Programs_Into23.pdf',
  },
  {
    id: 'wp-multilingual-ai-safety',
    type: 'Whitepaper',
    title: 'Whitepaper: Multilingual AI Safety Testing',
    description: 'A technical whitepaper on why native-speaker red teaming is the critical gap in AI safety programs - covering code-switching attacks, low-resource language vulnerabilities, regulatory context, and Into23\'s multilingual adversarial testing methodology.',
    format: 'PDF',
    pages: '18 pages',
    language: 'English',
    icon: 'file-text',
    pdfFile: '/whitepapers/Multilingual_AI_Safety_Testing_Whitepaper.pdf',
  },
  {
    id: 'sec-alibaba-cloud',
    type: 'Security Brief',
    title: 'Alibaba Cloud Security & Data Residency Overview',
    description: 'A practical overview of how Into23 uses Alibaba Cloud Infrastructure for security, regional deployment planning, and APAC-oriented data residency design. Covers enterprise hosting considerations, regional architecture, and safer client-facing guidance.',
    format: 'Markdown',
    language: 'English',
    icon: 'shield',
  },
  {
    id: 'sol-blockchain-web3',
    type: 'Solution Brief',
    title: 'Solution Brief: Blockchain & Web3 Localization',
    description: 'A short solution brief outlining how localization strategy, governance, and multilingual execution support blockchain and Web3 products entering global markets.',
    format: 'PDF',
    pages: '6 pages',
    language: 'English',
    icon: 'lightbulb',
    pdfFile: '/whitepapers/SolutionBrief_Blockchain_Web3_Localization.pdf',
  },
];

const filterCategories = ['All', 'Industry Report', 'Case Study', 'Whitepaper', 'Security Brief', 'Solution Brief'];

function ResourceIcon({ type }: { type: Resource['icon'] }) {
  if (type === 'chart') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-cobalt-glow">
        <path d="M3 3v16a2 2 0 0 0 2 2h16" />
        <path d="M18 17V9" />
        <path d="M13 17V5" />
        <path d="M8 17v-3" />
      </svg>
    );
  }
  if (type === 'book') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-cobalt-glow">
        <path d="M12 7v14" />
        <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
      </svg>
    );
  }
  if (type === 'file-text') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-cobalt-glow">
        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
        <path d="M14 2v4a2 2 0 0 0 2 2h4" />
        <path d="M10 9H8" />
        <path d="M16 13H8" />
        <path d="M16 17H8" />
      </svg>
    );
  }
  if (type === 'shield') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-cobalt-glow">
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      </svg>
    );
  }
  // lightbulb
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-cobalt-glow">
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
      <path d="M9 18h6" />
      <path d="M10 22h4" />
    </svg>
  );
}

export default function ResourcesPage() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);

  const filtered = activeFilter === 'All'
    ? resources
    : resources.filter(r => r.type === activeFilter);

  const handleDownload = (resource: Resource) => {
    if (!resource.pdfFile) return;
    setSelectedResource(resource);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* ===== HERO ===== */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(43,98,239,0.05), transparent, rgb(8,11,18))',
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">
              RESOURCE CENTER
            </span>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-silver-text leading-tight mb-6">
              Insights &amp; Tools for
              <span className="gradient-text"> Enterprise Teams</span>
            </h1>
            <p className="text-lg text-silver-muted leading-relaxed">
              Industry reports, security briefs, solution briefs, case studies, and playbooks to help you make informed decisions about AI translation and multilingual operations.
            </p>
          </div>
        </div>
      </section>

      {/* ===== FILTER TABS ===== */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 pb-8">
        <div className="flex flex-wrap gap-2">
          {filterCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeFilter === cat
                  ? 'bg-cobalt text-white' :'bg-graphite border border-steel/30 text-silver-muted hover:text-silver-text hover:border-cobalt/30'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* ===== RESOURCE CARDS GRID ===== */}
      <section className="py-8 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((resource) => (
              <div
                key={resource.id}
                className={`glow-card rounded-xl p-7 flex flex-col group ${resource.pdfFile ? 'cursor-pointer' : resource.caseStudyUrl ? 'cursor-pointer' : 'cursor-default'}`}
                style={{ opacity: 1, transform: 'none' }}
                onClick={() => {
                  if (resource.caseStudyUrl) {
                    window.location.href = resource.caseStudyUrl;
                  } else if (resource.pdfFile) {
                    handleDownload(resource);
                  }
                }}
              >
                {/* Card Header */}
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 rounded-lg bg-cobalt/10 border border-cobalt/20 flex items-center justify-center group-hover:bg-cobalt/20 transition-colors">
                    <ResourceIcon type={resource.icon} />
                  </div>
                  <span className="text-xs font-mono text-cobalt/70 tracking-wider uppercase">
                    {resource.type}
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-display font-semibold text-lg text-silver-text mb-3 group-hover:text-cobalt-glow transition-colors leading-snug">
                  {resource.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-silver-muted leading-relaxed mb-5 flex-1">
                  {resource.description}
                </p>

                {/* Footer */}
                <div className="pt-5 border-t border-steel/20 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs text-silver-muted flex-wrap">
                    <span className="px-2 py-1 rounded bg-steel/20 font-mono">{resource.format}</span>
                    {resource.pages && <span>{resource.pages}</span>}
                    <span className="px-2 py-1 rounded bg-cobalt/10 text-cobalt-glow border border-cobalt/20">
                      {resource.language}
                    </span>
                  </div>
                  {resource.caseStudyUrl ? (
                    <Link
                      href={resource.caseStudyUrl}
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1.5 text-cobalt text-sm font-medium hover:gap-2.5 transition-all"
                    >
                      Read Case Study
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </Link>
                  ) : resource.pdfFile ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDownload(resource); }}
                      className="flex items-center gap-1.5 text-cobalt text-sm font-medium hover:gap-2.5 transition-all"
                    >
                      Download
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </button>
                  ) : (
                    <div className="flex items-center gap-1.5 text-cobalt text-sm font-medium group-hover:gap-2.5 transition-all">
                      Download
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" x2="12" y1="15" y2="3" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div
            className="relative rounded-2xl overflow-hidden border border-cobalt/20 p-10 lg:p-16 text-center"
            style={{
              background: 'linear-gradient(135deg, rgba(43,98,239,0.12), rgb(8,11,18), rgb(19,22,28))',
            }}
          >
            <div className="absolute inset-0 grid-bg opacity-10" />
            <div className="relative">
              <h2 className="font-display font-bold text-3xl lg:text-4xl text-silver-text mb-4">
                Need a Custom Analysis?
              </h2>
              <p className="text-silver-muted text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
                Our team can prepare tailored reports, engine benchmarks, or cost analyses specific to your language pairs, content types, and volume requirements.
              </p>
              <Link href="/get-a-quote">
                <span className="inline-flex items-center gap-2 px-8 py-4 bg-cobalt hover:bg-cobalt-glow text-white font-semibold rounded-lg transition-all hover:shadow-[0_0_20px_rgba(43,98,239,0.3)]">
                  Get a Quote
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Download Modal */}
      <ResourceDownloadModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedResource(null); }}
        resource={selectedResource ? {
          title: selectedResource.title,
          pages: selectedResource.pages,
          language: selectedResource.language,
          pdfFile: selectedResource.pdfFile!,
          type: selectedResource.type,
        } : null}
      />
    </div>
  );
}
